import { useEffect } from 'react';
import { EVENT_NAMES } from '@config/dom';
import { addEvent, isDescendantOf, isEscape } from '@helpers/dom';
import { AppFCC } from '@types';
import { CloserProps } from './types';

/**
 * Triggers a callback on Escape and on click events originating outside its
 * "root", which is typically a parent component. Useful for closing a
 * dropdown or a popup.
 *
 * Usage:
 *
 * <Closer rootRef={this} onClose={this.close}>
 * ... content ...
 * </Closer>
 */
import { Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
export const Closer: AppFCC<CloserProps> = props => {
  const {
    children,
    rootRef,
    container,
    onClose
  } = props;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (isEscape(event)) {
        onClose(event);
      }
    };
    const onClick = (event: MouseEvent) => {
      /**
       * We pass and read the ref object from props here because
       * we want to read its mutable 'current' property.
       * It is changed after the render phase in a parent
       * component that uses Closer, which can cause 'ref.current
       * is undefined' error during the first render phase.
       */
      if (!rootRef.current) {
        return;
      }
      if (!isDescendantOf((event.target as Node), rootRef.current)) {
        onClose(((event as unknown) as MouseEvent));
      }
    };
    const unKeyDown = addEvent<KeyboardEvent>(container ?? window, EVENT_NAMES.KEYDOWN, onKeyDown, true);
    const unClick = addEvent<MouseEvent>(container ?? window, EVENT_NAMES.CLICK, onClick, true);
    return () => {
      unKeyDown();
      unClick();
    };
  }, [rootRef, container, onClose]);
  return _jsx(_Fragment, {
    children: children
  });
};